/**
 * The ONE `TimeframeValue` → `{from, to}` resolver for every Brand Perception
 * « Tableau de bord » endpoint. Lifted out of `BrandAxisDashboard.tsx` (lot 4) when
 * the prompt matrix (lot 8) became a second caller: two copies of this rule would
 * drift, and the bug it exists to prevent is invisible until it empties a view.
 *
 * Why not `resolveTimeframe` from `components/common/TimeframeSelector`: it sets
 * `from = to − days`, i.e. « 90 derniers jours » is an INCLUSIVE span of 91. The
 * API's `spanDays` is inclusive too, so `selectGrain(91)` returns `'month'` and the
 * same click that reads « 3 mois » quietly changes the precision of the whole
 * response. A `maxDays` on the selector does NOT catch it: that check is an
 * exclusive diff and admits a 91-day inclusive range. This is now a precision
 * boundary rather than an empty view (the cap is the full retention, see
 * `BP_MAX_SPAN_DAYS`), which makes it less destructive and no less surprising.
 *
 * `spanClamped` is returned rather than applied silently: shaving a window the user
 * picked and saying nothing reads as « il n'y a rien avant cette date ».
 *
 * It also owns `useBpTimeframe`, the SHARED selection behind every BP period control
 * (see the bottom of the file).
 */

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AUDIT_RESULTS_RETENTION_DAYS,
  BP_DASHBOARD_DAILY_TIER_MAX_DAYS,
} from '@traaker/shared';

import type { TimeframeValue } from '../common/TimeframeSelector';

/**
 * The longest window the picker offers: the FULL retention (730 d), mirroring the
 * API's own `resolveRange` clamp — so a span the UI allows is a span the service
 * will serve rather than silently shorten (2026-10-08).
 *
 * ⚠ It is NOT the reach of every card. Above `BP_DASHBOARD_DAILY_TIER_MAX_DAYS`
 * (90) the API's `selectGrain` switches the whole response to the MONTHLY tier, and
 * only two of the five fact families can answer there:
 *
 *  - `BPSERIES` (score + the three rates, the KPI row, `prev`, brand overlays) holds
 *    730 DAILY slots on one row at a 730-day TTL, and the reader folds them into
 *    whatever grain is asked — so the trend line is true over two years today;
 *  - `BPTGTAGG` (the per-target drill-down) is month-grained already.
 *
 * The other three — `BPTREND` (breakdowns, sentiment, alignment, topics, perception),
 * `BPPROMPT` (the prompt matrix) and `BPTOP` (Contenu / Produits) — are DAILY rows at
 * a 90-day TTL (`bp_facts.BP_DAILY_TTL_DAYS`) whose monthly counterparts
 * (`BPTRENDM#` / `BPPROMPTM#` / `BPTOPM#`) have **no writer yet**. Past 90 days they
 * come back empty, the response raises `monthly_tier_empty`, and every affected card
 * must say « le détail quotidien n'est plus conservé » — never « pas mesuré », which
 * would be a false claim about a period that WAS measured.
 */
export const BP_MAX_SPAN_DAYS = AUDIT_RESULTS_RETENTION_DAYS;

/**
 * Where the response changes precision. Exposed so a card can tell the two empty
 * states apart — "nothing was measured" vs "the detail has expired" — without
 * re-deriving the rule.
 */
export const BP_DAILY_REACH_DAYS = BP_DASHBOARD_DAILY_TIER_MAX_DAYS;

const MS_PER_DAY = 86_400_000;

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDaysUtc(day: string, n: number): string {
  return new Date(Date.parse(`${day}T00:00:00.000Z`) + n * MS_PER_DAY).toISOString().slice(0, 10);
}

/** Inclusive, like the API's `spanDays`: `from === to` is one day. */
function inclusiveSpan(from: string, to: string): number {
  return (
    Math.round(
      (Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`)) / MS_PER_DAY,
    ) + 1
  );
}

export function resolveBpRange(tf: TimeframeValue): {
  from: string;
  to: string;
  spanClamped: boolean;
} {
  const to = tf.kind === 'relative' ? todayUtc() : tf.to;
  if (tf.kind === 'relative') {
    const n = Math.min(tf.days, BP_MAX_SPAN_DAYS);
    return { from: addDaysUtc(to, -(n - 1)), to, spanClamped: tf.days > BP_MAX_SPAN_DAYS };
  }
  if (inclusiveSpan(tf.from, to) > BP_MAX_SPAN_DAYS) {
    return { from: addDaysUtc(to, -(BP_MAX_SPAN_DAYS - 1)), to, spanClamped: true };
  }
  return { from: tf.from, to, spanClamped: false };
}

// ---------------------------------------------------------------------------
// The shared selection (2026-10-06)
// ---------------------------------------------------------------------------

/**
 * ONE period selection for the whole Brand Perception surface: the « Tableau de bord »
 * of each axis AND the prompt matrix in « Configurer ». Each control used to own a
 * local `useState`, so every move between the three axes or between the two views
 * silently snapped the window back to 30 days — and the number on screen changed
 * without the user touching the control that is supposed to explain it.
 *
 * Same contract as the `view` toggle in `BrandAxisPage` (deliberately, so the two
 * controls on one toolbar cannot behave differently):
 *  - **the URL param wins**, so a shared link lands on the window it names;
 *  - **localStorage remembers** the last choice, which is what carries the selection
 *    across an axis change — sidebar navigation builds a fresh URL with no params;
 *  - **the default is 30 days**, and a value that does not parse falls back to it
 *    rather than to a window nobody picked.
 *
 * A change writes BOTH, and writes the URL with `replace: true`: a period is a view
 * setting, not a navigation step, so it must not stack history entries the back button
 * then has to walk through.
 *
 * Nothing is written on mount. A URL with no `tf` keeps the remembered value rather
 * than rewriting the address bar behind the user's back.
 */
export const BP_TIMEFRAME_PARAM = 'tf';
const BP_TIMEFRAME_STORAGE_KEY = 'traaker.brandAxis.timeframe';
export const BP_TIMEFRAME_DEFAULT: TimeframeValue = { kind: 'relative', days: 30 };

/** The one wire form, shared by the URL param and localStorage: `30d` | `2026-08-01..2026-09-01`. */
export function serializeBpTimeframe(tf: TimeframeValue): string {
  return tf.kind === 'relative' ? `${tf.days}d` : `${tf.from}..${tf.to}`;
}

const RELATIVE_RE = /^(\d{1,4})d$/;
const ABSOLUTE_RE = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;

/**
 * `null` — never a guess — for anything that does not parse. Both sources are
 * user-editable (an address bar, a stale localStorage from an older build), so a
 * half-understood value must not become a window the user never asked for; the caller
 * falls back to the default. The span is NOT checked here: `resolveBpRange` clamps it
 * and reports `spanClamped`, which is the one place that rule lives.
 */
export function parseBpTimeframe(raw: string | null | undefined): TimeframeValue | null {
  if (!raw) return null;
  const relative = RELATIVE_RE.exec(raw);
  if (relative) {
    const days = Number(relative[1]);
    return days >= 1 ? { kind: 'relative', days } : null;
  }
  const absolute = ABSOLUTE_RE.exec(raw);
  if (!absolute) return null;
  const [, from, to] = absolute;
  // `2026-13-45` matches the shape but is not a date.
  if (Number.isNaN(Date.parse(`${from}T00:00:00.000Z`))) return null;
  if (Number.isNaN(Date.parse(`${to}T00:00:00.000Z`))) return null;
  return from <= to ? { kind: 'absolute', from, to } : null;
}

export function useBpTimeframe(): [TimeframeValue, (tf: TimeframeValue) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get(BP_TIMEFRAME_PARAM);
  const raw = fromUrl ?? localStorage.getItem(BP_TIMEFRAME_STORAGE_KEY);

  // Memoized on the SERIALIZED form, not recomputed per render: the value is derived
  // state (there is no `useState` here), and a fresh object every render would
  // invalidate every `useMemo`/effect keyed on the timeframe itself.
  const timeframe = useMemo(() => parseBpTimeframe(raw) ?? BP_TIMEFRAME_DEFAULT, [raw]);

  const setTimeframe = useCallback(
    (tf: TimeframeValue) => {
      const next = serializeBpTimeframe(tf);
      localStorage.setItem(BP_TIMEFRAME_STORAGE_KEY, next);
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set(BP_TIMEFRAME_PARAM, next);
          return p;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return [timeframe, setTimeframe];
}
